import { TextField } from "@/components/ui/text-field";

// The donor's `DimensionField` — a width, height or depth box.
//
// Source: `timmy/src/utils/DimensionField.tsx`, and it is **shared**: both item
// dialogs draw their three dimensions with it, and so does `AssetEditForm`. So it
// ports here rather than into either dialog
// (`docs/agents/migration-guidelines.md`, "Shared donor components port as
// shared Atlas components").
//
// Three things travel with it, all from `utils/validation.ts`:
//
//   - `type='number'` and `min: 0` (`dimensionInputProps`), which is what arms
//     the `:out-of-range` painting inside `TextField` — the *browser's* range
//     check, a beat ahead of the form's, since the spinner can put -1 in the box
//     before `onChange` reports it;
//   - `dimensionValidationRules`, which is `dimensionProblem` in
//     `@/lib/catalog-item-form-rules` — the caller's, because a form owns when
//     its messages become visible;
//   - `dimensionErrorSx`, the one thing this component paints itself: the
//     message under a dimension field sits 8px below it and flush with its edge,
//     where MUI's default helper text sits 3px below and 14px in. The donor
//     overrides it for exactly these fields, so it belongs to exactly this
//     component.
//
// The label is the caller's and deliberately not defaulted: the Request Item
// dialog's three read "Width (inches)" and the Add Variant dialog's three read
// "Width", and the donor spells both out at the call site.

export interface DimensionFieldProps {
  label: string;
  value: string;
  onChange: (next: string) => void;
  error?: string;
  disabled?: boolean;
  "data-testid"?: string;
}

export function DimensionField({
  label,
  value,
  onChange,
  error,
  disabled,
  "data-testid": testId,
}: DimensionFieldProps) {
  return (
    <TextField
      data-testid={testId}
      disabled={disabled}
      label={label}
      type="number"
      min={0}
      value={value}
      onChange={onChange}
      error={error}
      // `dimensionErrorSx` (`utils/validation.ts:40-47`): 12px, 8px above,
      // nothing below, no padding.
      //
      // **It names no `marginLeft`**, so MUI's own `contained` helper text
      // survives underneath it — `FormHelperText.js:74-78` gives `marginLeft:
      // 14, marginRight: 14` whenever the FormControl variant is `outlined`,
      // which is `InputField`'s default. The donor's dimension error therefore
      // sits indented under the input text like every other field's, and the
      // base `mx-3.5` here is that same 14px. An earlier `mx-0` pulled it flush
      // to the frame's edge, out of line with the non-dimension errors beside it.
      messageSpacing="loose"
    />
  );
}
